// ═══════════════════════════════════════════════════════════════
// HISTORIAL DE PLANILLAS TSS
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Download, ChevronDown, ChevronUp, RefreshCw,
  FileText, Users, AlertCircle,
} from 'lucide-react';
import { getTSSHistorial, getTSSPlanillaRecords, downloadTSSPlanilla } from '../../../../api/tss';

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ESTADO_BADGE = {
  generada:   'bg-blue-100 text-blue-700',
  descargada: 'bg-green-100 text-green-700',
};

export default function PlanillaHistorial({ companyId }) {
  const [historial,       setHistorial]       = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [expandedId,      setExpandedId]      = useState(null);
  const [expandedRecords, setExpandedRecords] = useState([]);
  const [loadingRecords,  setLoadingRecords]  = useState(false);
  const [downloading,     setDownloading]     = useState(null);
  const [error,           setError]           = useState(null);

  const cargar = () => {
    setLoading(true);
    getTSSHistorial(companyId)
      .then(res => setHistorial(res.data || []))
      .catch(() => setError('Error al cargar historial'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [companyId]); // eslint-disable-line

  const handleExpand = async (planilla) => {
    if (expandedId === planilla.id) {
      setExpandedId(null);
      setExpandedRecords([]);
      return;
    }
    setExpandedId(planilla.id);
    setLoadingRecords(true);
    try {
      const res = await getTSSPlanillaRecords(planilla.id);
      setExpandedRecords(res.data || []);
    } catch {
      setExpandedRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleDownload = async (planilla) => {
    setDownloading(planilla.id);
    try {
      await downloadTSSPlanilla(planilla.id, planilla.nombre_archivo);
      // Actualizar estado en UI
      setHistorial(prev => prev.map(p =>
        p.id === planilla.id ? { ...p, estado: 'descargada' } : p
      ));
    } catch {
      setError('Error al descargar planilla');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <RefreshCw size={18} className="animate-spin mr-2" />
      Cargando historial...
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{historial.length} planilla(s) generada(s)</p>
        <button onClick={cargar} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800">
          <RefreshCw size={12} /> Actualizar
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {historial.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <FileText className="w-9 h-9 text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No hay planillas generadas aún</p>
          <p className="text-xs text-gray-400 mt-1">Genera tu primera planilla desde la pestaña "Generar Planilla"</p>
        </div>
      )}

      {historial.map(p => (
        <div key={p.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Fila principal */}
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-gray-800">{p.periodo}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ESTADO_BADGE[p.estado] || 'bg-gray-100 text-gray-600'}`}>
                  {p.estado}
                </span>
                <span className="text-xs text-gray-500">{p.tipo_archivo}</span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <Users size={11} />
                  {p.total_empleados} empleados
                </span>
                <span>Sal. Cotizable: RD$ {fmt(p.total_salario_cotizable)}</span>
                <span>{new Date(p.fecha_generacion).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' })}</span>
                {p.usuario_nombre && <span>por {p.usuario_nombre}</span>}
              </div>
              {p.nombre_archivo && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">{p.nombre_archivo}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleExpand(p)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {expandedId === p.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {expandedId === p.id ? 'Ocultar' : 'Ver detalle'}
              </button>
              <button
                onClick={() => handleDownload(p)}
                disabled={!!downloading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {downloading === p.id
                  ? <RefreshCw size={11} className="animate-spin" />
                  : <Download size={11} />
                }
                TXT
              </button>
            </div>
          </div>

          {/* Detalle expandible */}
          {expandedId === p.id && (
            <div className="border-t border-gray-100">
              {loadingRecords ? (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <RefreshCw size={14} className="animate-spin mr-2" /> Cargando empleados...
                </div>
              ) : (
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 bg-gray-100">
                      <tr className="text-gray-600">
                        <th className="px-3 py-2 text-left font-semibold">Código</th>
                        <th className="px-3 py-2 text-left font-semibold">Empleado</th>
                        <th className="px-3 py-2 text-left font-semibold">Cédula</th>
                        <th className="px-3 py-2 text-right font-semibold">Sal. Cotizable</th>
                        <th className="px-3 py-2 text-right font-semibold">Sal. ISR</th>
                        <th className="px-3 py-2 text-right font-semibold">INFOTEP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expandedRecords.map((r, i) => (
                        <tr key={r.id} className={`border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                          <td className="px-3 py-1.5 text-gray-500">{r.clave_nomina}</td>
                          <td className="px-3 py-1.5 font-medium text-gray-800">
                            {r.nombres} {r.primer_apellido} {r.segundo_apellido}
                          </td>
                          <td className="px-3 py-1.5 text-gray-600">{r.numero_doc || '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">RD$ {fmt(r.salario_cotizable)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">RD$ {fmt(r.salario_isr)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">RD$ {fmt(r.salario_infotep)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
